import { Component } from 'react';
import { connect } from 'react-redux';
import BezierEasing from 'bezier-easing';
import { GeneralSlice } from '@/redux/general';

export const SLIDE_ANIMATION_MS = 200;

// The wrapper must never scroll: slides are positioned via transform only. With `overflow: hidden`
// the box is still programmatically scrollable, so focusing an input in an off-screen slide (e.g. the
// Instagram in-app browser's "Auto-fill with saved info?" touching the form fields on first view)
// scrolled it onto an opacity-0 slide and the slider went blank. `clip` (Safari 16+, Chrome 90+,
// Firefox 81+) forbids that scrolling; older engines fall back to `hidden` + resetWrapperScroll.
const WRAPPER_OVERFLOW =
  typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('overflow', 'clip')
    ? 'clip'
    : 'hidden';

class MySlider extends Component {
  constructor(props) {
    super(props);
    this.easing = BezierEasing(0.4, 0.0, 0.2, 1);
  }

  /**
   * @access private
   *
   * @param {number} duration
   * @param {number} fps
   * @param {number} lastStep
   */
  calculateParams(duration, fps, lastStep) {
    const width = this.refs.rootRef.getBoundingClientRect().width;

    if (this.refs.oldRef) {
      this.current = {
        pos: this.refs.oldPos,
        height: this.refs.oldRef.getBoundingClientRect().height,
      };
    }

    // current scroll position
    const scrollFrom = window.scrollY === undefined ? document.documentElement.scrollTop : window.scrollY;

    const topEndReference = lastStep
      ? document.getElementsByClassName('banner__label-content')[0]
      : this.refs.newRef.getElementsByClassName('slide-question')[0];

    const top =
      topEndReference === undefined
        ? this.refs.newRef.getBoundingClientRect().top
        : topEndReference.getBoundingClientRect().top;

    const bottom = this.refs.newRef.getBoundingClientRect().bottom;

    // position of question, do not want to scroll past that
    const scrollMax = scrollFrom + top;

    // putting bottom of slide into frame
    const scrollTarget = bottom > scrollFrom + window.innerHeight ? bottom - window.innerHeight : scrollFrom;

    // Back on the first slide the whole page head (logo, hero) belongs in view again, not just the question.
    const scrollTo = this.refs.newPos === 0 ? 0 : scrollTarget > scrollMax ? scrollMax : scrollTarget;

    /**
     * @access private
     *
     * @typedef AnimationParams
     * @type {object}
     * @property {number} start
     * @property {number} duration
     * @property {number} frameTime
     * @property {object} from
     * @property {number} from.scroll
     * @property {number} from.left
     * @property {number} from.height
     * @property {object} to
     * @property {number} to.scroll
     * @property {number} to.left
     * @property {number} to.height
     *
     * @type {AnimationParams}
     */
    this.animation = {
      start: Date.now(),
      duration: duration,
      frameTime: Math.ceil(1000 / fps),
      from: {
        scroll: scrollFrom !== scrollTo ? scrollFrom : null,
        left: width * this.refs.oldPos * -1,
        height: this.current.height,
      },
      to: {
        scroll: scrollFrom !== scrollTo ? scrollTo : null,
        left: width * this.refs.newPos * -1,
        height: this.refs.newRef.getBoundingClientRect().height,
      },
    };
  }

  /**
   * @access public
   */
  run() {
    if (this.isRunning()) {
      return;
    }

    this.running = true;
    this.animation.start = Date.now();
    // A restarted animation (navigating again mid-transition) must not leave the old loop running.
    this.loopId = (this.loopId || 0) + 1;
    const loopId = this.loopId;
    requestAnimationFrame(() => this.tick(loopId));
  }

  /**
   * @access private
   */
  tick(loopId) {
    if (loopId !== this.loopId) return;
    if (!this.refs.rootRef || !this.refs.wrapperRef) {
      this.stop();
      return;
    }

    let progressTime = (Date.now() - this.animation.start) / this.animation.duration;
    if (progressTime >= 1) {
      this.update(1);
      this.stop();
      // Animation is over: hide the outgoing slide and settle on the measured final layout.
      this.applyRestingState();
      return;
    }

    this.update(progressTime);

    if (progressTime < 1) {
      requestAnimationFrame(() => this.tick(loopId));
    }
  }

  /**
   * @access private
   * @param {number} progressTime
   * @return {number}
   */
  update(progressTime) {
    let animationProgress = this.easing(progressTime);
    const leftPos = this.animation.from.left + (this.animation.to.left - this.animation.from.left) * animationProgress;
    this.refs.rootRef.style.transform = 'translateX(' + leftPos + 'px)';
    this.refs.wrapperRef.style.height =
      this.animation.from.height + (this.animation.to.height - this.animation.from.height) * animationProgress + 'px';

    if (this.animation.from.scroll !== null && this.animation.to.scroll !== null) {
      const scrollPos =
        this.animation.from.scroll + (this.animation.to.scroll - this.animation.from.scroll) * animationProgress;
      window.scrollTo(0, scrollPos);
    }

    const now = Date.now();
    this.frameTimes.push(now);

    return now;
  }

  /**
   * @access public
   * @return {boolean}
   */
  isRunning() {
    return this.running;
  }

  /**
   * @access private
   */
  stop() {
    //alert(`avg fps ${1000 / (this.animation.duration / this.frameTimes.length)} showing ${this.frameTimes.length} frames`);
    this.running = false;
  }

  animate(duration, oldPos, newPos) {
    this.refs = {
      wrapperRef: this.wrapperRef,
      rootRef: this.rootRef,
      oldRef: this.childRefs[oldPos],
      oldPos: oldPos,
      newRef: this.childRefs[newPos],
      newPos: newPos,
    };

    const lastStep = this.props.children.length - 1 === this.props.position;

    // Both slides stay visible while sliding; the incoming one must be visible before calculateParams
    // measures its height (hidden slides collapse to height 0 via `.slide[data-visible='false'] > *`).
    [this.refs.oldRef, this.refs.newRef].forEach((r) => this.setSlideVisible(r, true));

    this.calculateParams(duration, 60, lastStep);
    /** @type {FrameTimes} */
    this.frameTimes = [];
    this.easing = BezierEasing(0.4, 0.0, 0.2, 1);
    /** @type {boolean} */
    this.running = false;

    this.props.beforeChange && this.props.beforeChange(oldPos, newPos);
    this.run();
    setTimeout(() => {
      this.props.afterChange && this.props.afterChange();
    }, 0);
  }

  resetWrapperScroll = (event) => {
    const wrapper = event.currentTarget;
    if (wrapper.scrollLeft !== 0) wrapper.scrollLeft = 0;
    if (wrapper.scrollTop !== 0) wrapper.scrollTop = 0;
  };

  onResize = () => this.adjustSlides();

  componentDidMount() {
    window.addEventListener('resize', this.onResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
  }

  shouldComponentUpdate(nextProps) {
    const prevProps = this.props;

    if (prevProps.children !== nextProps.children) {
      return true;
    } else if (prevProps.position !== nextProps.position) {
      this.animate(SLIDE_ANIMATION_MS, prevProps.position, this.props.position);
    }

    return false;
  }

  componentDidUpdate(prevProps) {
    if (this.childRefs && this.rootRef && this.sliderRef) {
      const positionChanged = prevProps.position !== this.props.position;

      if (prevProps.children !== this.props.children) {
        // Not adjustSlides() when the position changed too: it would snap to the target slide right
        // before animate() slides there, which made the transition jump.
        if (positionChanged) this.layoutSlides();
        else this.adjustSlides();
      }

      if (positionChanged) {
        this.animate(SLIDE_ANIMATION_MS, prevProps.position, this.props.position);
      }
    }
  }

  adjustSlides() {
    if (!this.layoutSlides()) return;
    // While a transition runs it owns transform, height and visibility; it settles them when it ends.
    // Snapping here (e.g. on a re-render mid-transition) caused the visible jump to the target slide.
    if (this.isRunning()) return;
    this.applyRestingState();
  }

  /**
   * Sizes the strip and the slides to the current width. Does not move or show/hide anything.
   * @return {boolean} whether the refs were available
   */
  layoutSlides() {
    if (!(this.childRefs && this.rootRef && this.sliderRef)) return false;
    const width = this.rootRef.getBoundingClientRect().width;
    this.sliderRef.style.width = `${width * this.childRefs.length}px`;

    this.childRefs.forEach((r) => {
      if (r) {
        r.style.width = `${width}px`;
        r.style.float = 'left';
      }
    });
    return true;
  }

  /**
   * Shows only the current slide and puts strip and wrapper at its final position and height.
   */
  applyRestingState() {
    if (!(this.childRefs && this.rootRef && this.wrapperRef)) return;
    const width = this.rootRef.getBoundingClientRect().width;

    this.childRefs.forEach((r, i) => this.setSlideVisible(r, i === this.props.position));

    this.rootRef.style.transform = `translateX(${width * this.props.position * -1}px)`;
    const current = this.childRefs[this.props.position];
    if (current) this.wrapperRef.style.height = current.getBoundingClientRect().height + 'px';
  }

  setSlideVisible(r, visible) {
    if (!r) return;
    r.setAttribute('data-visible', visible);
    r.style.opacity = visible ? 1 : 0;
  }

  render() {
    this.childRefs = [];
    const slides = this.props.children.map((c, i) => (
      <div
        className='slide'
        key={i}
        ref={(r) => {
          this.childRefs[i] = r;
        }}
      >
        {' '}
        {c}
      </div>
    ));

    return (
      <div
        className='slides-wrapper'
        style={{
          color: window.getComputedStyle(document.body)['backgroundColor'],
          overflow: WRAPPER_OVERFLOW,
          // `overflow: clip` (unlike `hidden`) does not establish a BFC; keep containing the floated slides.
          display: 'flow-root',
        }}
        onScroll={this.resetWrapperScroll}
        ref={(r) => {
          this.wrapperRef = r;
        }}
      >
        <div
          className='slides-root'
          ref={(r) => {
            this.rootRef = r;
          }}
        >
          <div
            className='slides-list'
            ref={(r) => {
              this.sliderRef = r;
            }}
          >
            {slides}
          </div>
        </div>
      </div>
    );
  }
}

MySlider.displayName = 'MySlider';

const mapStateToProps = (state) => ({
  extendedSliderHeight: GeneralSlice.selectors.extendedSliderHeight(state),
});

export default connect(mapStateToProps)(MySlider);
